// Exports: AddTaskMemberModal
import React, { useMemo, useState } from "react";
import { useAlert } from "components/UI_components/Alert";
import useTaskApi from "hooks/useTaskApi";

const AddTaskMemberModal = ({
  task,
  siteMembers,
  getInitials,
  onClose,
  onMembersAdded,
  preserveScroll,
}) => {
  const { addAlert } = useAlert();
  const { addTaskMembers, removeTaskMember } = useTaskApi();
  const [selectedMemberIds, setSelectedMemberIds] = useState(() =>
    (task.members || []).map((member) => Number(member.member_id))
  );

  const normalizedSiteMembers = useMemo(
    () =>
      (siteMembers || []).map((member) => ({
        ...member,
        member_id: Number(member.member_id ?? member.id),
      })),
    [siteMembers]
  );

  const currentlyAssignedMemberIds = useMemo(
    () => new Set((task.members || []).map((member) => Number(member.member_id))),
    [task.members]
  );

  const handleMemberSelect = (member) => {
    const memberId = Number(member.member_id);
    setSelectedMemberIds((current) =>
      current.includes(memberId)
        ? current.filter((candidateId) => candidateId !== memberId)
        : [...current, memberId]
    );
  };

  const handleAddMembers = async () => {
    const selectedSet = new Set(selectedMemberIds);
    const currentSet = new Set(
      (task.members || []).map((member) => Number(member.member_id))
    );
    const toAddIds = [...selectedSet].filter((memberId) => !currentSet.has(memberId));
    const toRemoveTaskMemberIds = (task.task_members || [])
      .filter((taskMember) => !selectedSet.has(Number(taskMember.member_id)))
      .map((taskMember) => taskMember.taskmember_id);

    if (toAddIds.length === 0 && toRemoveTaskMemberIds.length === 0) {
      onClose();
      return;
    }

    await preserveScroll(`[data-scroll-anchor="task-${task.id}"]`, async () => {
      try {
        if (toAddIds.length > 0) {
          const response = await addTaskMembers({
            task_id: task.id,
            member_ids: toAddIds,
          });
          const data = await response.json();

          if (data?.detail) {
            addAlert(
              "You do not have permission to add members to task. You must have Task Authority Permission",
              "error"
            );
            return;
          }
        }

        if (toRemoveTaskMemberIds.length > 0) {
          await Promise.all(
            toRemoveTaskMemberIds.map((taskMemberId) =>
              removeTaskMember(taskMemberId, { shouldAlert: false })
            )
          );
        }

        const selectedMembers = normalizedSiteMembers.filter((member) =>
          selectedSet.has(Number(member.member_id))
        );
        await onMembersAdded(selectedMembers);
        setSelectedMemberIds([]);
        onClose();
      } catch (error) {
        console.error("Error adding task members:", error);
      }
    });
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-lg p-6 w-96"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Add Task Members</h2>
          <button
            className="text-gray-500 hover:text-gray-700"
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        <div className="max-h-60 overflow-y-auto mb-4">
          {normalizedSiteMembers.length > 0 ? (
            normalizedSiteMembers.map((member) => {
              const isSelected = selectedMemberIds.includes(Number(member.member_id));
              const wasAssigned = currentlyAssignedMemberIds.has(Number(member.member_id));
              return (
              <div
                key={member.member_id}
                className={`flex items-center justify-between px-3 py-2 rounded cursor-pointer hover:bg-gray-100 ${
                  isSelected ? "bg-gray-200" : ""
                }`}
                onClick={() => handleMemberSelect(member)}
              >
                <div className="flex items-center">
                  {member?.userpic ? (
                    <img
                      src={member.userpic}
                      alt={`${member.first_name} ${member.last_name}`}
                      className="w-8 h-8 rounded-full border-2 border-white mr-2"
                    />
                  ) : (
                    <span
                      className="w-8 h-8 rounded-full bg-[#343C6A] text-white font-medium text-sm flex items-center justify-center mr-2"
                      title={`${member.first_name} ${member.last_name}`}
                    >
                      {getInitials(`${member.first_name} ${member.last_name}`)}
                    </span>
                  )}
                  <span className="text-sm text-gray-700">
                    {member.first_name} {member.last_name}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span
                    className={`text-[11px] font-medium ${
                      isSelected ? "text-purple-700" : "text-gray-400"
                    }`}
                  >
                    {isSelected
                      ? wasAssigned
                        ? "Assigned"
                        : "Will be assigned"
                      : wasAssigned
                      ? "Will be unassigned"
                      : "Unassigned"}
                  </span>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onClick={(event) => event.stopPropagation()}
                    onChange={() => handleMemberSelect(member)}
                    className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                  />
                </div>
              </div>
            );
            })
          ) : (
            <div className="text-gray-500 text-sm">No organization members found.</div>
          )}
        </div>

        <div className="flex justify-end space-x-2">
          <button
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
            onClick={handleAddMembers}
          >
            Add Members
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddTaskMemberModal;
